import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Users, CalendarCheck, TrendingUp, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function Gate({ title, body, action }) {
  return (
    <div className="flex min-h-screen items-center justify-center px-6" data-testid="admin-gate">
      <div className="max-w-md border border-[#33240E]/15 bg-[#FFF9EA] p-10 text-center">
        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.35em] text-[#8A5A0F]">Be Kind Community Inc</p>
        <h1 className="mb-4 font-serif text-3xl font-black text-[#33240E]">{title}</h1>
        <p className="mb-8 text-sm font-light text-muted-foreground">{body}</p>
        {action}
        <div className="mt-6">
          <Link to="/" data-testid="admin-back-link" className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8A5A0F] hover:text-[#33240E]">
            ← Back to the site
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, openAuth } = useAuth();
  const [data, setData] = useState(null);
  const [activations, setActivations] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/participants").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load participants."));
      api.get("/activations").then((r) => setActivations(r.data)).catch(() => {});
    }
  }, [user]);

  const elapsed = useMemo(() => {
    if (!activations) return 0;
    const d = new Date();
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return Math.max(1, activations.days.filter((x) => x.date <= iso && x.countdown > 0).length);
  }, [activations]);

  if (loading) return null;
  if (!user) {
    return (
      <Gate title="Admin sign-in" body="This board is for Be Kind Community admins. Sign in with your admin account to review participation."
        action={<button onClick={() => openAuth("login")} data-testid="admin-signin-button"
          className="bg-[#33240E] px-8 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#F6E7C1] transition-colors hover:bg-[#4A3517]">Sign In</button>} />
    );
  }
  if (user.role !== "admin") {
    return <Gate title="Admins only" body={`You're signed in as ${user.email}, which isn't an admin account.`} action={null} />;
  }

  const participants = data?.participants || [];
  const filtered = participants.filter((p) =>
    `${p.name} ${p.email}`.toLowerCase().includes(q.toLowerCase())
  );
  const totalCheckins = participants.reduce((s, p) => s + p.total, 0);
  const avgStreak = participants.length ? (participants.reduce((s, p) => s + p.streak, 0) / participants.length).toFixed(1) : 0;

  return (
    <div className="min-h-screen px-6 py-28 lg:px-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.4em] text-[#8A5A0F]">Kindness Ball · Wave One — October 1</p>
            <h1 className="font-serif text-4xl font-black text-[#33240E] sm:text-5xl">VIP Review <span className="italic text-[#8A5A0F]">Board</span></h1>
            <p className="mt-3 max-w-lg text-sm font-light text-muted-foreground">
              Honor-system check-ins, ranked. Consistency at the top is your invitation list.
            </p>
          </div>
          <Link to="/" data-testid="admin-back-link" className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8A5A0F] hover:text-[#33240E]">← Back to the site</Link>
        </div>

        <div className="mb-10 grid grid-cols-2 gap-px bg-[#33240E]/10 lg:grid-cols-4">
          {[
            { icon: Users, label: "Members", value: participants.length, tid: "admin-stat-members" },
            { icon: CalendarCheck, label: "Total check-ins", value: totalCheckins, tid: "admin-stat-checkins" },
            { icon: Flame, label: "Avg streak", value: avgStreak, tid: "admin-stat-streak" },
            { icon: TrendingUp, label: "Days elapsed", value: elapsed, tid: "admin-stat-elapsed" },
          ].map((c) => (
            <div key={c.label} className="bg-[#FFF9EA] p-6" data-testid={c.tid}>
              <c.icon className="mb-3 h-4 w-4 text-[#8A5A0F]" />
              <p className="font-serif text-3xl font-black text-[#33240E]">{c.value}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="mb-6 flex items-center gap-3 border border-[#33240E]/15 bg-[#FFF9EA] px-4 py-3">
          <Trophy className="h-4 w-4 shrink-0 text-[#8A5A0F]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or email..."
            data-testid="admin-search-input"
            className="w-full bg-transparent text-sm text-[#33240E] outline-none placeholder:text-muted-foreground"
          />
        </div>

        <div className="overflow-x-auto border border-[#33240E]/15 bg-[#FFF9EA]">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-[#33240E]/15 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                <th className="px-5 py-4">#</th>
                <th className="px-5 py-4">Member</th>
                <th className="px-5 py-4">Check-ins</th>
                <th className="px-5 py-4">Streak</th>
                <th className="px-5 py-4">Last active</th>
                <th className="px-5 py-4">Consistency</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => (
                <tr key={p.id} className="border-b border-[#33240E]/10 transition-colors hover:bg-[#FDF3D8]" data-testid={`admin-row-${i}`}>
                  <td className="px-5 py-4 font-mono text-xs text-muted-foreground">{i + 1}</td>
                  <td className="px-5 py-4">
                    <p className="font-semibold text-[#33240E]">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.email}</p>
                  </td>
                  <td className="px-5 py-4 font-serif text-xl font-bold text-[#33240E]">{p.total}</td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1.5 font-mono text-xs text-[#33240E]">
                      <Flame className={`h-3.5 w-3.5 ${p.streak > 0 ? "text-[#9E3C3C]" : "text-muted-foreground/40"}`} />
                      {p.streak}d
                    </span>
                  </td>
                  <td className="px-5 py-4 font-mono text-xs text-muted-foreground">{p.dates.length ? p.dates[p.dates.length - 1] : "—"}</td>
                  <td className="px-5 py-4">
                    <div className="h-1 w-28 bg-[#33240E]/10">
                      <div className="h-1 bg-[#8A5A0F]" style={{ width: `${Math.min(100, (p.total / elapsed) * 100)}%` }} />
                    </div>
                    <p className="mt-1 font-mono text-[10px] text-muted-foreground">{Math.round((p.total / elapsed) * 100)}% of days</p>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-muted-foreground" data-testid="admin-empty">No members match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          Honor-system check-ins · sorted by total, then streak
        </p>
      </div>
    </div>
  );
}
